import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { settingsFocusRing } from 'features/settings/components/settingsTheme';
import { Section, btnPrimary, linkBtn } from 'features/settings/components/panels/askCreditsShared';

/**
 * Billing v2 Pro upgrade — flat $249/mo (or annual), with optional trial code.
 *
 * Self-contained on purpose: the legacy bucket pricing panel keeps serving the
 * existing GROWTH subscriptions untouched until the D3 conversion, so this
 * renders alongside rather than editing that file. It hides itself entirely
 * when the server has no Pro price configured, so a half-configured
 * environment shows the old world rather than a broken button.
 *
 * A trial code grants 14 days and makes the card optional at checkout; without
 * one, checkout collects payment immediately. The code is validated
 * server-side before any Stripe object is created, so a wrong code costs
 * nothing and returns a specific message.
 */

const API_URL = process.env.REACT_APP_API_SERVER_URL || 'https://backend.trodo.ai';
const INK = 'var(--text-primary)';
const MUTED = 'var(--text-muted)';

/** What Pro includes, from the server's plan presets. */
function proFeatures(pro) {
  if (!pro) return [];
  const n = (v) => Number(v).toLocaleString('en-US');
  const mb = pro.max_payload_bytes / (1024 * 1024);
  return [
    `${n(pro.traces)} traces / month`,
    `${n(pro.executions)} executions (evals, agents, alerts)`,
    `$${n(pro.ask_credit_usd)} Lucid credit / month`,
    `${pro.retention_days}-day retention · ${mb >= 1 ? `${mb} MB` : `${Math.round(mb * 1024)} KB`} payloads`,
  ];
}

/** "$12 per 100,000 units" from the live Stripe price — never hardcoded. */
function overagePhrase(entry, noun) {
  if (!entry || entry.amount == null) return null;
  const amount = `$${Number(entry.amount).toFixed(2).replace(/\.00$/, '')}`;
  const n = Number(entry.per || 1);
  if (n === 1) return `${amount} per ${noun.replace(/s$/, '')}`;
  return `${amount} per ${n.toLocaleString('en-US')} ${noun}`;
}

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const ProUpgradeCard = ({ teamId, onAvailabilityChange }) => {
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [cycle, setCycle] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  /** Trial entry stays hidden until asked for — most visitors are upgrading,
   *  not redeeming, and a code box shown to everyone reads as a price you are
   *  failing to get. */
  const [redeeming, setRedeeming] = useState(false);
  const [code, setCode] = useState('');

  const loadStatus = useCallback(async () => {
    if (!teamId) return;
    try {
      const res = await axios.get(`${API_URL}/api/billing/v2/status`, {
        params: { teamId },
        headers: authHeaders(),
      });
      setStatus(res.data);
    } catch {
      // Never surface a status failure as a billing error — the card simply
      // stays hidden and the legacy panel remains the user's route.
      setStatus(null);
    } finally {
      setLoaded(true);
    }
  }, [teamId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const startCheckout = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/billing/v2/checkout`,
        { teamId, cycle, code: redeeming ? code.trim() || undefined : undefined },
        { headers: authHeaders() },
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError('Checkout did not return a URL. Try again.');
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not start checkout. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // Only FREE teams are offered an upgrade — a paid, trialing or enterprise
  // team renders its own billing view instead.
  const isFree = !status?.plan || status.plan === 'FREE';

  // Tell the parent, if it still cares. Kept for callers that gate layout on
  // whether this card renders.
  useEffect(() => {
    if (onAvailabilityChange) onAvailabilityChange(isFree && Boolean(status?.configured));
  }, [isFree, status?.configured, onAvailabilityChange]);

  if (!isFree || !loaded) return null;

  // Nothing to sell yet — say so rather than rendering nothing. This card is
  // now the whole billing page for a free team; hiding it on a
  // misconfiguration left them staring at an empty panel with no way to pay.
  if (!status?.configured) {
    return (
      <Section title="Free plan">
        <p className="text-[12px]" style={{ color: MUTED }}>
          Upgrades aren&apos;t available right now. Contact{' '}
          <a href="mailto:support@trodo.ai" className={linkBtn}>support@trodo.ai</a>.
        </p>
      </Section>
    );
  }

  const price = cycle === 'annual' ? status?.pricing?.pro?.annual : status?.pricing?.pro?.monthly;
  const u = overagePhrase(status?.pricing?.overage?.units, 'traces');
  const i = overagePhrase(status?.pricing?.overage?.executions, 'executions');

  return (
    <>
      <Section
        title="Upgrade to Pro"
        action={(
          <div
            className="inline-flex rounded-md border border-[color:var(--border-default)] p-0.5"
            role="group"
            aria-label="Billing cycle"
          >
            {['monthly', 'annual'].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCycle(c)}
                aria-pressed={cycle === c}
                className={`rounded px-2.5 py-1 text-[11px] font-medium capitalize transition-colors ${settingsFocusRing} ${
                  cycle === c
                    ? 'border border-[color:var(--border-strong)] text-[color:var(--text-primary)]'
                    : 'border border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      >
        <div className="flex items-baseline gap-1.5">
          <span className="text-[24px] font-[450] leading-none tracking-tight tabular-nums" style={{ color: INK }}>
            {price == null ? '—' : `$${Number(price).toLocaleString('en-US')}`}
          </span>
          <span className="text-[12px]" style={{ color: MUTED }}>/{cycle === 'annual' ? 'year' : 'month'}</span>
        </div>

        <ul
          className="mt-3.5 list-disc space-y-1 pl-4 text-[12px] leading-snug marker:text-[color:var(--border-strong)]"
          style={{ color: MUTED }}
        >
          {proFeatures(status?.plans?.pro).map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>

        {(u || i) && (
          <p className="mt-3 text-[12px] leading-relaxed" style={{ color: MUTED }}>
            Optional overage{u ? ` at ${u}` : ''}{u && i ? ' and ' : ''}{!u && i ? ' at ' : ''}{i || ''}.
          </p>
        )}

        {redeeming && (
          <div className="mt-3.5">
            <label htmlFor="trial-code" className="mb-1 block text-[11px]" style={{ color: MUTED }}>
              Trial code
            </label>
            <input
              id="trial-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter your code"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && code.trim() && !busy) startCheckout(); }}
              className={`h-8 w-full max-w-[280px] rounded-md border border-[color:var(--border-default)] bg-transparent px-2.5 text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] transition-[border-color] duration-150 focus:border-[color:var(--border-strong)] focus:outline-none ${settingsFocusRing}`}
            />
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={startCheckout}
            disabled={busy || (redeeming && !code.trim())}
            className={btnPrimary}
          >
            {busy ? 'Starting…' : redeeming ? 'Start free trial' : 'Upgrade to Pro'}
          </button>
          <button
            type="button"
            onClick={() => { setRedeeming((v) => !v); setError(null); if (redeeming) setCode(''); }}
            className={linkBtn}
          >
            {redeeming ? 'Cancel' : 'Redeem a trial code'}
          </button>
        </div>

        {redeeming && !error && (
          <p className="mt-2 text-[11px]" style={{ color: MUTED }}>
            Card taken up front. Cancel before the trial ends to avoid a charge.
          </p>
        )}

        {error && (
          <p className="mt-2 text-[12px]" style={{ color: 'var(--feedback-error)' }} role="alert">
            {error}
          </p>
        )}
      </Section>

      <Section
        title="Enterprise"
        action={<a href="mailto:support@trodo.ai?subject=Trodo%20Enterprise" className={linkBtn}>Contact us</a>}
      >
        <p className="text-[12px]" style={{ color: MUTED }}>
          Pooled usage, a contracted allowance, custom retention and invoiced billing.
        </p>
      </Section>
    </>
  );
};

export default ProUpgradeCard;
